import React, {
  ReactChild,
  ReactChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { app } from "api";
import { Flex, Spinner, useToast } from "@chakra-ui/react";
import { useQueryCache } from "react-query";
import { registerWebNotification } from "api/auth";
interface AuthContextInterface {
  auth: boolean | null;
  user?: firebase.default.User;
  lab?: firebase.default.firestore.DocumentData;
}

const AuthContext = React.createContext<AuthContextInterface>({
  auth: null,
});

interface AuthProviderProps {
  children: ReactChildren | ReactChild;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryCache = useQueryCache();
  const [store, setStore] = useState<AuthContextInterface>({
    auth: null,
  });
  const toast = useToast();

  async function getUserClaims(user: firebase.default.User) {
    return await user.getIdTokenResult();
  }

  async function getLabData(id: string) {
    return (await app.firestore().collection("labs").doc(id).get()).data();
  }

  useEffect(() => {
    return app.auth().onAuthStateChanged((user) => {
      if (user) {
        registerWebNotification()
        Promise.all([getUserClaims(user), getLabData(user.uid)]).then(
          ([token, lab]) => {
            if (!!token.claims.lab) {
              setStore({ auth: true, user, lab });
            } else {
              toast({
                status: "error",
                title: "Invalid User",
                description: "Use appropriate cura health app to login",
                duration: 3000,
                isClosable: true,
              });
              app.auth().signOut();
            }
          }
        );
      } else {
        setStore({ auth: false });
        queryCache.invalidateQueries();
      }
    });
  }, []);

  useEffect(() => {
    if (store.user) {
      return app
        .firestore()
        .collection("labs")
        .doc(store.user.uid)
        .onSnapshot((snap) => setStore((s) => ({ ...s, lab: snap.data() })));
    }
  }, [store.user]);

  if (store.auth === null)
    return (
      <Flex
        minH="100vh"
        bg="gray.100"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner color="teal.500" size="xl" thickness="4px" />
      </Flex>
    );

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextInterface {
  const authContext = useContext(AuthContext);
  
  return authContext;
}


